import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/apiClient';
import usePublisher from '../../hooks/usePublisher';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import * as S from './Styles';
import FileUpload from '../../helper/FileUpload';
import uploadIcon from '/src/assets/images/application/folder-file-up.svg?url';

import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeftOutlined';
import { Typography, Box } from '@mui/material';
import Text from '../Texts';
import { legalFields } from './fields';
import * as R from './rules';
import NumberConverter from '../../helper/ConvertPersionNumberToEnglish';
import SubmitButton from '../Button/SubmitButton';
import { FormSkeleton } from '../Feedback/Loaders';
import ContractInfoModal from './ContractInfoModal';
import NationalIdInfoModal from './NationalIdInfoModal';

interface FormValues {
  name: string;
  display_name: string;
  national_id: string;
  address: string;
  postal_code: string;
  phone: string;
  national_id_image: string | null;
}

interface EditLegalIdentityProps {
  onNext: () => void;
  publisherId: number;
}

const EditLegalIdentity: React.FC<EditLegalIdentityProps> = ({
  onNext,
  publisherId,
}) => {
  const queryClient = useQueryClient();
  const [backendErrors, setBackendErrors] = useState<any>({});
  const [selectedFile1, setSelectedFile1] = useState<File | null>(null);
  const [selectedFile2, setSelectedFile2] = useState<File | null>(null);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isNationalIdModalOpen, setIsNationalIdModalOpen] = useState(false);

  // Fetch publisher data directly
  const { data: publisher, isLoading, isError } = usePublisher(publisherId);

  // استفاده از hook برای مدیریت submit
  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات با موفقیت به‌روزرسانی شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
    onSuccess: async () => {
      if (publisher) {
        // Invalidate و refetch داده‌های publisher
        await queryClient.invalidateQueries({
          queryKey: ['publisher', publisher.id],
        });
      }

      // تاخیر قبل از navigate
      setTimeout(() => {
        onNext();
      }, 800);
    },
  });

  // NOTE: useFormik must run on every render, so the loading/error returns
  // below sit *after* it. Returning before a hook changes the hook order
  // between renders, which React rejects.
  // `draft` carries only the columns whose edit is awaiting review; anything
  // else falls through to the live value.
  const staged = (column: string) =>
    publisher?.draft && column in publisher.draft
      ? publisher.draft[column]
      : (publisher as Record<string, any> | undefined)?.[column];

  const formik = useFormik({
    // Per field: an edit to an approved identity is held for review, so the
    // plain fields are still the live values. Prefer the staged one where it
    // exists, or a publisher reopening this form sees their change missing.
    initialValues: {
      name: staged('name') || '',
      display_name: staged('display_name') || '',
      national_id: staged('national_id') || '',
      address: staged('address') || '',
      postal_code: staged('postal_code') || '',
      phone: staged('phone') || '',
      national_id_image: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('نام شرکت الزامی است'),
      display_name: Yup.string().required('نام نمایشی الزامی است'),
      // A company posts a شناسه ملی, which is eleven digits with its own check
      // digit — not the ten-digit personal کد ملی this used to demand.
      national_id: Yup.string()
        .required('شناسه ملی الزامی است')
        .matches(R.LEGAL_ID, R.LEGAL_ID_MESSAGE),
      address: Yup.string().required('آدرس الزامی است'),
      postal_code: Yup.string()
        .required('کد پستی الزامی است')
        .matches(R.POSTAL_CODE, R.POSTAL_CODE_MESSAGE),
      phone: Yup.string()
        .required('تلفن ثابت الزامی است')
        .matches(R.LANDLINE_PHONE, R.LANDLINE_PHONE_MESSAGE),
    }),
    onSubmit: async (values) => {
      // بررسی validation errors
      const errors = await formik.validateForm(values);
      if (Object.keys(errors).length > 0) {
        toast.error('لطفاً تمام فیلدهای الزامی را پر کنید');
        return;
      }

      if (!publisher) return;

      await handleSubmit(async () => {
        setBackendErrors({});

        const formData = new FormData();

        // اضافه کردن فیلدهای متنی
        Object.keys(values).forEach((key) => {
          if (
            (values as { [key: string]: any })[key] !== undefined &&
            (values as { [key: string]: any })[key] !== null
          ) {
            formData.append(
              key,
              String((values as { [key: string]: any })[key]),
            );
          }
        });

        // The api picks the `national_id` rule from this: without it every
        // submit was checked against the ten-digit personal کد ملی and a real
        // company شناسه ملی was rejected. This form is only ever reached for a
        // legal publisher, so the fallback matches the screen.
        formData.append('identity_type', publisher.identity_type || 'LEGAL');

        // اضافه کردن فایل‌ها (اگر انتخاب شده باشند)
        if (selectedFile1) {
          formData.append('national_id_image', selectedFile1);
        }
        if (selectedFile2) {
          formData.append('contract', selectedFile2);
        }

        console.log('Submitting form data:', Object.fromEntries(formData));
        const response = await apiClient.post(
          `/v2/publisher/update/${publisher.id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        console.log('Update Response:', response.data);
        return response.data;
      });
    },
  });

  if (isLoading) {
    return (
      <S.Container>
        <FormSkeleton fields={7} />
      </S.Container>
    );
  }

  if (isError || !publisher) {
    return (
      <S.Container>
        <S.Description variant="body1">
          خطا در بارگذاری اطلاعات
        </S.Description>
      </S.Container>
    );
  }

  return (
    <S.Container>
      {/* Was PersonalIdentty.title, which reads "هویت حقیقی" on the legal form.
          editTitle because this is the edit form, not creation. */}
      <S.Title variant="h6">{Text.legalIdentty.editTitle}</S.Title>
      <S.Description variant="body1">
        {Text.legalIdentty.completedData}
      </S.Description>

      <form onSubmit={formik.handleSubmit} noValidate>
        <S.GridContainer>
          {legalFields.map((field) => (
            <S.FieldContainer key={field.name}>
              <S.Label>{field.label}</S.Label>
              <S.StyledTextField
                variant="outlined"
                placeholder={field.placeholder}
                size="small"
                name={field.name}
                value={formik.values[field.name as keyof FormValues]}
                onChange={
                  field.numeric
                    ? (event) =>
                        formik.setFieldValue(
                          field.name,
                          NumberConverter.toNumericOnly(event.target.value),
                        )
                    : formik.handleChange
                }
                onBlur={formik.handleBlur}
                error={
                  !!formik.touched[field.name as keyof FormValues] &&
                  !!formik.errors[field.name as keyof FormValues]
                }
                // Gated on `touched` like `error` above is. Without it the
                // message showed on a field the user had not reached yet, so a
                // freshly loaded form claimed a valid value was invalid.
                helperText={
                  (formik.touched[field.name as keyof FormValues] &&
                    formik.errors[field.name as keyof FormValues]) ||
                  backendErrors[field.name]
                }
              />
            </S.FieldContainer>
          ))}
        </S.GridContainer>

        <S.UploadContainer>
          <S.FieldContainer>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
            >
              <S.Label>{Text.legalIdentty.ImageIdentity}</S.Label>
              <Typography
                component="span"
                sx={{
                  color: '#1E88E5',
                  fontSize: '12px',
                  fontFamily: 'IRANYekanXFaNum',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  marginTop: '2px',
                  '&:hover': {
                    color: '#1565C0',
                  },
                }}
                onClick={() => setIsNationalIdModalOpen(true)}
              >
                توضیحات
              </Typography>
            </Box>
            <FileUpload
              onFilesSelected={(files) => {
                if (files.length > 0) {
                  setSelectedFile1(files[0]);
                  toast.success(`فایل ${files[0].name} انتخاب شد`);
                }
              }}
              imageSrc={uploadIcon}
              uploadText={Text.legalIdentty.uploadImage}
            />
          </S.FieldContainer>

          <S.FieldContainer>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
            >
              <S.Label>تصویر معرفی نامه شرکت با سربرگ رسمی</S.Label>
              <Typography
                component="span"
                sx={{
                  color: '#1E88E5',
                  fontSize: '12px',
                  fontFamily: 'IRANYekanXFaNum',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  marginTop: '2px',
                  '&:hover': {
                    color: '#1565C0',
                  },
                }}
                onClick={() => setIsContractModalOpen(true)}
              >
                توضیحات
              </Typography>
            </Box>
            <FileUpload
              onFilesSelected={(files) => {
                if (files.length > 0) {
                  setSelectedFile2(files[0]);
                  toast.success(`فایل ${files[0].name} انتخاب شد`);
                }
              }}
              imageSrc={uploadIcon}
              uploadText="آپلود تصویر معرفی نامه شرکت با سربرگ رسمی"
              acceptedFileTypes={['.pdf']}
            />
          </S.FieldContainer>
        </S.UploadContainer>

        <S.SubmitButtonBox>
          <SubmitButton
            type="submit"
            startIcon={<ArrowCircleLeft />}
            isSubmitting={isSubmitting}
            loadingText="در حال ارسال..."
          >
            {Text.PersonalIdentty.submite}
          </SubmitButton>
        </S.SubmitButtonBox>
      </form>

      <ContractInfoModal
        open={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
      />

      <NationalIdInfoModal
        open={isNationalIdModalOpen}
        onClose={() => setIsNationalIdModalOpen(false)}
      />
    </S.Container>
  );
};

export default EditLegalIdentity;
